/**
 * Git Branch Storage Utility
 * Centralized management of git branch/ref storage in localStorage
 */

import { DEFAULT_MICROSERVICE_BRANCH } from '../constants/gitConstants';

const GIT_REF_PREFIX = 'git_ref_';

/**
 * Generate storage key for a service's git reference
 * @param {string} projectId - Mia Platform project ID
 * @param {string} serviceName - Service name
 * @returns {string} Storage key
 */
export const getGitRefStorageKey = (projectId, serviceName) => {
  if (!projectId || !serviceName) {
    return null;
  }
  return `${GIT_REF_PREFIX}${projectId}_${serviceName}`;
};

/**
 * Get the current git branch/ref for a service
 * @param {string} projectId - Mia Platform project ID
 * @param {string} serviceName - Service name
 * @param {string} [fallback='main'] - Fallback value if not found
 * @returns {string} Git branch/ref name
 */
export const getGitRef = (projectId, serviceName, fallback = 'main') => {
  const key = getGitRefStorageKey(projectId, serviceName);
  if (!key) {
    return fallback;
  }

  const savedRef = localStorage.getItem(key);
  return savedRef || fallback;
};

/**
 * Save the current git branch/ref for a service
 * @param {string} projectId - Mia Platform project ID
 * @param {string} serviceName - Service name
 * @param {string} ref - Git branch/ref name
 * @returns {boolean} Success status
 */
export const setGitRef = (projectId, serviceName, ref) => {
  const key = getGitRefStorageKey(projectId, serviceName);
  if (!key || !ref) {
    return false;
  }

  try {
    localStorage.setItem(key, ref);
    return true;
  } catch (error) {
    console.error('Failed to save git ref to localStorage:', error); /* eslint-disable-line no-console */
    return false;
  }
};

/**
 * Remove the git branch/ref for a service
 * @param {string} projectId - Mia Platform project ID
 * @param {string} serviceName - Service name
 * @returns {boolean} Success status
 */
export const removeGitRef = (projectId, serviceName) => {
  const key = getGitRefStorageKey(projectId, serviceName);
  if (!key) {
    return false;
  }

  try {
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    console.error('Failed to remove git ref from localStorage:', error); /* eslint-disable-line no-console */
    return false;
  }
};

/**
 * Get all stored git refs
 * @returns {Object} Map of service keys to git refs
 */
export const getAllGitRefs = () => {
  const refs = {};

  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(GIT_REF_PREFIX)) {
        const value = localStorage.getItem(key);
        if (value) {
          refs[key] = value;
        }
      }
    }
  } catch (error) {
    console.error('Failed to read git refs from localStorage:', error); /* eslint-disable-line no-console */
  }

  return refs;
};

/**
 * Get git branch for a resource object
 * Handles multiple fallback strategies
 * @param {Object} resource - Resource object (selectedResource)
 * @param {string} projectId - Mia Platform project ID
 * @param {string} [fallback='main'] - Final fallback value
 * @returns {string} Git branch/ref name
 */
export const getResourceGitBranch = (resource, projectId, fallback = 'main') => {
  if (!resource || !projectId) {
    return fallback;
  }

  // Extract service name from different possible locations
  const serviceName = resource.data?.name || resource.name;
  if (!serviceName) {
    return fallback;
  }

  // Try to get from localStorage first (most reliable)
  const savedRef = getGitRef(projectId, serviceName, null);
  if (savedRef) {
    return savedRef;
  }

  // Fallback to resource data if available
  if (resource.data?.branch) {
    return resource.data.branch;
  }

  if (resource.branch) {
    return resource.branch;
  }

  // Use default microservice branch from constants
  return DEFAULT_MICROSERVICE_BRANCH;
};

/**
 * Clear all git refs from localStorage
 * Useful for debugging or cleanup
 */
export const clearAllGitRefs = () => {
  try {
    const keys = Object.keys(localStorage).filter(k => k.startsWith(GIT_REF_PREFIX));
    keys.forEach(key => localStorage.removeItem(key));
    return true;
  } catch (error) {
    console.error('Failed to clear git refs from localStorage:', error); /* eslint-disable-line no-console */
    return false;
  }
};

export default {
  getGitRefStorageKey,
  getGitRef,
  setGitRef,
  removeGitRef,
  getAllGitRefs,
  getResourceGitBranch,
  clearAllGitRefs
};

